import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { useApp } from '../context/AppContext';
import { getTrips } from '../services/api';
import TripDetailDialog from '../components/TripDetailDialog';
import MeterPhotoViewer, { MeterCheckBadge } from '../components/MeterPhotoViewer';
import { firstOfMonthLocalISO, toLocalISODate } from '../utils/dateUtils';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'ok', label: 'OK' },
  { value: 'warning', label: 'Warning' },
  { value: 'suspicious', label: 'Suspicious' },
];

export default function DriveLogs() {
  const { vehicles, drivers, isAdmin, authReady } = useApp();

  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState(() => firstOfMonthLocalISO());
  const [to, setTo] = useState(() => toLocalISODate());
  const [status, setStatus] = useState('');
  const [vehicleId, setVehicleId] = useState('');
  const [driverId, setDriverId] = useState('');
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [photoTrip, setPhotoTrip] = useState(null);

  const loadTrips = useCallback(async (filters) => {
    setLoading(true);
    try {
      const params = {};
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      if (filters.status) params.status = filters.status;
      if (filters.vehicleId) params.vehicleId = filters.vehicleId;
      if (filters.driverId) params.driverId = filters.driverId;

      const rows = await getTrips(params);
      setTrips(Array.isArray(rows) ? rows : []);
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Could not load drive logs.');
      setTrips([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authReady) return;
    if (!isAdmin) {
      setTrips([]);
      setLoading(false);
      return;
    }
    loadTrips({
      from: firstOfMonthLocalISO(),
      to: toLocalISODate(),
    });
  }, [authReady, isAdmin, loadTrips]);

  const handleApply = () => {
    if (from && to && from > to) {
      toast.error('From date must be on or before To date.');
      return;
    }
    loadTrips({ from, to, status, vehicleId, driverId });
  };

  const vehicleOptions = useMemo(
    () => [...vehicles].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [vehicles]
  );

  const driverOptions = useMemo(
    () => [...drivers].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [drivers]
  );

  if (!authReady) {
    return <p className="text-muted">Checking session…</p>;
  }

  if (!isAdmin) {
    return (
      <div>
        <div className="page-header">
          <h2>Drive Logs</h2>
          <p>Admin access is required to view trip history.</p>
        </div>
        <div className="card">
          <p className="text-muted">Sign in with an admin account to browse completed and active trips.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-header">
        <h2>Drive Logs</h2>
        <p>Browse trips by date range, vehicle, driver, and status</p>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-title">Filters</div>
        <div className="form-grid cols3">
          <div className="form-group">
            <label>From date</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} max={to || undefined} />
          </div>
          <div className="form-group">
            <label>To date</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} min={from || undefined} />
          </div>
          <div className="form-group">
            <label>Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Vehicle</label>
            <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
              <option value="">All vehicles</option>
              {vehicleOptions.map((v) => (
                <option key={v._id} value={v._id}>{v.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Driver</label>
            <select value={driverId} onChange={(e) => setDriverId(e.target.value)}>
              <option value="">All drivers</option>
              {driverOptions.map((d) => (
                <option key={d._id} value={d._id}>{d.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ marginTop: 16 }}>
          <button type="button" className="btn btn-primary" onClick={handleApply} disabled={loading}>
            {loading ? 'Loading…' : 'Apply filters'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="card"><p className="text-muted">Loading drive logs…</p></div>
      ) : trips.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="icon">📋</div>
            <p>No trips match the current filters.</p>
          </div>
        </div>
      ) : (
        <>
          <p className="text-muted text-sm" style={{ marginBottom: 12 }}>
            Showing {trips.length} trip{trips.length === 1 ? '' : 's'} · click a row for full details
          </p>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Vehicle</th>
                  <th>Driver</th>
                  <th>Route</th>
                  <th>Out / In</th>
                  <th>Expected</th>
                  <th>Actual</th>
                  <th>Diff</th>
                  <th>Status</th>
                  <th>Meter check</th>
                </tr>
              </thead>
              <tbody>
                {trips.map((t) => (
                  <tr
                    key={t._id}
                    className="clickable-row"
                    onClick={() => setSelectedTrip(t)}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedTrip(t);
                      }
                    }}
                  >
                    <td>{format(new Date(t.date), 'dd MMM yyyy')}</td>
                    <td>{t.vehicle?.name || '—'}</td>
                    <td>{t.driver?.name || '—'}</td>
                    <td>{t.stops?.length ? t.stops.map((s) => s.name).join(' → ') : '—'}</td>
                    <td className="mono text-sm">{t.outTime || '—'} / {t.inTime || '—'}</td>
                    <td className="mono">{t.expectedKM ?? '—'}</td>
                    <td className="mono">{t.actualKM ?? '—'}</td>
                    <td className="mono">
                      {t.difference != null
                        ? (t.difference > 0 ? `+${t.difference}` : String(t.difference))
                        : '—'}
                    </td>
                    <td><span className={`badge ${t.status}`}>{t.status}</span></td>
                    <td>
                      {t.meterInPhoto ? (
                        <div className="meter-check-cell">
                          <MeterCheckBadge trip={t} />
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPhotoTrip(t);
                            }}
                          >
                            View photo
                          </button>
                        </div>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <TripDetailDialog trip={selectedTrip} onClose={() => setSelectedTrip(null)} />
          <MeterPhotoViewer trip={photoTrip} onClose={() => setPhotoTrip(null)} />
        </>
      )}
    </>
  );
}
